// src/merch/templates.js
//
// Design templates = PATTERN × PALETTE + editable name / number text.
//
// Everything is drawn procedurally at runtime (no image assets), so a
// template is just a few lines of data and prints crisp at any size.
//
// IP note: palettes are named by their COLOURS ("Black & Gold"), never by
// team or city. Colour combos alone are fine; team names, logos, crests
// and wordmarks are not. Keep it that way when adding palettes.

import { renderTextLayer, TEXT_DEFAULTS } from "../engine";

export const PALETTES = [
  { id: "black-gold",   label: "Black & Gold",    primary: "#111111", secondary: "#FFB81C", accent: "#ffffff", text: "#FFB81C", outline: "#111111" },
  { id: "royal-white",  label: "Royal & White",   primary: "#003E7E", secondary: "#ffffff", accent: "#A2AAAD", text: "#ffffff", outline: "#003E7E" },
  { id: "red-black",    label: "Red & Black",     primary: "#C8102E", secondary: "#111111", accent: "#ffffff", text: "#ffffff", outline: "#111111" },
  { id: "red-white",    label: "Red & White",     primary: "#C8102E", secondary: "#ffffff", accent: "#111111", text: "#ffffff", outline: "#8a0b20" },
  { id: "navy-silver",  label: "Navy & Silver",   primary: "#041E42", secondary: "#A2AAAD", accent: "#ffffff", text: "#ffffff", outline: "#041E42" },
  { id: "blue-orange",  label: "Blue & Orange",   primary: "#0038A8", secondary: "#F47A38", accent: "#ffffff", text: "#ffffff", outline: "#0038A8" },
  { id: "orange-black", label: "Orange & Black",  primary: "#FC4C02", secondary: "#111111", accent: "#ffffff", text: "#ffffff", outline: "#111111" },
  { id: "green-white",  label: "Green & White",   primary: "#006847", secondary: "#ffffff", accent: "#111111", text: "#ffffff", outline: "#004a33" },
  { id: "purple-gold",  label: "Purple & Gold",   primary: "#552583", secondary: "#FDB927", accent: "#ffffff", text: "#FDB927", outline: "#2e1450" },
  { id: "teal-black",   label: "Teal & Black",    primary: "#006D75", secondary: "#111111", accent: "#ffffff", text: "#ffffff", outline: "#111111" },
  { id: "maroon-white", label: "Maroon & White",  primary: "#6F263D", secondary: "#ffffff", accent: "#A2AAAD", text: "#ffffff", outline: "#4a1929" },
  { id: "sky-navy",     label: "Sky & Navy",      primary: "#6CACE4", secondary: "#041E42", accent: "#ffffff", text: "#041E42", outline: "#ffffff" },
];

export const PATTERNS = [
  { id: "stripes", label: "Stripes" },
  { id: "split",   label: "Split" },
  { id: "chevron", label: "Chevron" },
  { id: "jersey",  label: "Jersey" },
  { id: "fade",    label: "Fade" },
  { id: "clean",   label: "Clean" },
];

export const getPalette = (id) => PALETTES.find((p) => p.id === id) || PALETTES[0];

/** Draw a pattern filling w×h. `round` = puck (circle), for the Clean border. */
export function drawPattern(ctx, w, h, patternId, pal, { round = false } = {}) {
  const s = Math.min(w, h);
  ctx.save();
  ctx.fillStyle = pal.primary;
  ctx.fillRect(0, 0, w, h);

  switch (patternId) {
    case "stripes": {
      ctx.translate(w / 2, h / 2);
      ctx.rotate(-0.45);
      const L = Math.hypot(w, h);
      const band = s * 0.16, edge = s * 0.025;
      // short stickers: a bold pair; long ones (shafts): pairs repeat along
      const offsets = w / h > 3
        ? (() => { const out = []; const period = s * 2.2; for (let x = -L; x < L; x += period) out.push(x, x + band * 1.9); return out; })()
        : [-0.18 * L, 0.08 * L];
      for (const x of offsets) {
        ctx.fillStyle = pal.accent;
        ctx.fillRect(x - edge, -L, band + edge * 2, L * 2);
        ctx.fillStyle = pal.secondary;
        ctx.fillRect(x, -L, band, L * 2);
      }
      break;
    }
    case "split": {
      const poly = (inset) => {
        ctx.beginPath();
        ctx.moveTo(w * 0.62 + inset, 0);
        ctx.lineTo(w, 0);
        ctx.lineTo(w, h);
        ctx.lineTo(w * 0.46 + inset, h);
        ctx.closePath();
      };
      ctx.fillStyle = pal.accent;
      poly(-s * 0.05);
      ctx.fill();
      ctx.fillStyle = pal.secondary;
      poly(0);
      ctx.fill();
      break;
    }
    case "chevron": {
      const step = s * 0.34, depth = s * 0.28;
      ctx.lineJoin = "miter";
      for (let x = w * 0.52; x < w + step; x += step) {
        for (const [col, lw] of [[pal.accent, s * 0.14], [pal.secondary, s * 0.09]]) {
          ctx.strokeStyle = col;
          ctx.lineWidth = lw;
          ctx.beginPath();
          ctx.moveTo(x - depth, -s * 0.1);
          ctx.lineTo(x, h / 2);
          ctx.lineTo(x - depth, h + s * 0.1);
          ctx.stroke();
        }
      }
      break;
    }
    case "jersey": {
      const y0 = h * 0.62, y1 = h * 0.84, pin = s * 0.02;
      ctx.fillStyle = pal.secondary;
      ctx.fillRect(0, y0, w, y1 - y0);
      ctx.fillStyle = pal.accent;
      ctx.fillRect(0, y0 - pin * 2.5, w, pin);
      ctx.fillRect(0, y1 + pin * 1.5, w, pin);
      break;
    }
    case "fade": {
      const g = ctx.createLinearGradient(0, 0, w, 0);
      g.addColorStop(0, pal.primary);
      g.addColorStop(0.55, pal.primary);
      g.addColorStop(1, pal.secondary);
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);
      // halftone dots that grow toward the right edge
      const cell = s * 0.07;
      ctx.fillStyle = pal.secondary;
      for (let y = cell / 2; y < h + cell; y += cell) {
        for (let x = w * 0.25; x < w + cell; x += cell) {
          const t = (x - w * 0.25) / (w * 0.75);
          const r = cell * 0.48 * Math.max(0, Math.min(1, t));
          if (r < 0.6) continue;
          ctx.beginPath();
          ctx.arc(x + ((Math.round(y / cell) % 2) * cell) / 2, y, r, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      break;
    }
    case "clean":
    default: {
      // inset border that follows the sticker shape
      const inset = s * 0.14, lw = s * 0.035;
      ctx.strokeStyle = pal.secondary;
      ctx.lineWidth = lw;
      ctx.beginPath();
      if (round) ctx.ellipse(w / 2, h / 2, w / 2 - inset, h / 2 - inset, 0, 0, Math.PI * 2);
      else ctx.roundRect(inset, inset, w - inset * 2, h - inset * 2, s * 0.08);
      ctx.stroke();
      break;
    }
  }
  ctx.restore();
}

/**
 * Build engine layers for a template (index 0 = top).
 * The background covers the print area PLUS bleed so the cut never shows
 * bare vinyl.
 */
export async function buildTemplateLayers({ patternId, paletteId, area, sticker, part = null, bleedMm = 3, name = "YOUR NAME", number = "00" }) {
  const pal = getPalette(paletteId);
  const aw = area.x1 - area.x0;
  const ah = area.y1 - area.y0;
  const bx = (bleedMm / sticker.widthMm) * aw;
  const by = (bleedMm / sticker.heightMm) * ah;
  const awB = aw + bx * 2, ahB = ah + by * 2;

  // background at the same pixel density as the 2048 design square
  const W = Math.round(awB * 2048), H = Math.round(ahB * 2048);
  const c = document.createElement("canvas");
  c.width = W; c.height = H;
  drawPattern(c.getContext("2d"), W, H, patternId, pal, { round: !!sticker.round });
  const bgUrl = c.toDataURL("image/png");

  const cx = (area.x0 + area.x1) / 2, cy = (area.y0 + area.y1) / 2;
  const aspect = sticker.widthMm / sticker.heightMm;
  // layout by part: shaft strip / blade / other wide / round-ish
  const role = part === "shaft" || aspect > 8 ? "shaft" : part === "blade" ? "blade" : aspect > 1.8 ? "wide" : "round";
  const layers = [];

  // text fitted inside a box (fractions of the print area): never taller or
  // wider than the space it's given, whatever the sticker shape
  const txt = async (label, settings, box) => {
    const text = { ...TEXT_DEFAULTS, ...settings };
    const { url, width, height, refEdge } = await renderTextLayer(text);
    const m = Math.max(width, height) || 1;
    const bw = box.w * aw, bh = box.h * ah;
    const scale = Math.min(bw / (width / m), bh / (height / m));
    layers.push({
      name: label, url, kind: "text", text, refEdge,
      transform: { tx: area.x0 + box.x * aw, ty: area.y0 + box.y * ah, scale, rot: 0 },
    });
  };

  const numStr = String(number || "").trim();
  const nameStr = String(name || "").trim();
  const numStyle = { fontKey: "block", color: pal.text, outlineColor: pal.outline, outlineWidth: 10, letterSpacing: 4 };
  const nameStyle = { fontKey: "varsity", color: pal.text, outlineColor: pal.outline, outlineWidth: 8, letterSpacing: 6 };

  const L = {
    // shaft (front reads blade → butt, left → right): name mid-shaft, number toward the butt
    shaft: { num: { x: 0.86, y: 0.5, w: 0.1, h: 0.7 }, name: { x: 0.5, y: 0.5, w: 0.42, h: 0.6 } },
    // blade: big number in the tall part, name alongside; clear of the thin heel
    blade: { num: { x: 0.3, y: 0.52, w: 0.24, h: 0.6 }, name: { x: 0.62, y: 0.46, w: 0.3, h: 0.3 } },
    wide: { num: { x: 0.83, y: 0.5, w: 0.2, h: 0.72 }, name: { x: 0.4, y: 0.5, w: 0.5, h: 0.5 } },
    round: { num: { x: 0.5, y: 0.44, w: 0.5, h: 0.46 }, name: { x: 0.5, y: 0.78, w: 0.62, h: 0.2 } },
  }[role];

  if (numStr) await txt(`#${numStr}`, { ...numStyle, text: numStr }, L.num);
  if (nameStr) await txt(nameStr, { ...nameStyle, text: nameStr, curve: role === "round" ? -35 : 0 }, L.name);

  layers.push({
    name: `${pal.label} ${PATTERNS.find((p) => p.id === patternId)?.label || ""}`.trim(),
    url: bgUrl,
    kind: "template-bg",
    transform: { tx: cx, ty: cy, scale: Math.max(awB, ahB), rot: 0 },
  });

  return layers;
}
